<template>
    <div
        v-if="product"
        ref="productContent"
        :class="{
            'flex flex-col md:flex-row md:gap-12 md:justify-center': productsCount === 1,
        }"
        class="product__content overflow-hidden opacity-0"
    >
        <div
            :class="{
                'md:w-[357px]': productsCount === 1,
            }"
            class="product__image relative flex justify-center items-center bg-white rounded-2xl p-3 h-[254px] flex-shrink-0"
        >
            <a v-if="product.link && product.image" :href="product.link" target="_blank">
                <img v-if="product.image && product.image.url" :src="product.image.url" :alt="`Image ${product.name}`" />
            </a>
            <img v-else-if="!product.link && product.image && product.image.url" :src="product.image.url" :alt="`Image ${product.name}`" />

            <button class="product__share-button absolute top-3 right-3 w-10 h-10 transition-all duration-500 hover:scale-110" @click="copyLinkToClipboard">
                <img src="~@/static/images/icons/share.svg" alt="Partager" />
            </button>
            <div v-if="translations" ref="toast" class="product__toast absolute left-0 right-0 top-1/2 bg-blue/80 h3 text-white mx-3 py-5 transform -translate-y-1/2 text-center rounded hidden opacity-0">
                {{ translations.results.toast }}
            </div>
        </div>
        <div
            :class="{
                'md:w-[357px]': productsCount === 1,
            }"
        >
            <div ref="productContainer" class="product__container overflow-hidden">
                <div ref="productFull" class="product__full">
                    <div ref="productPreview" class="product__preview">
                        <div class="flex justify-between items-start mt-4">
                            <p v-if="product.name" class="body text-white">
                                <strong>{{ product.name }}</strong>
                            </p>
                        </div>
                        <div class="flex flex-col gap-y-3 mt-4">
                            <div v-if="product.region" class="flex items-center gap-x-2">
                                <img src="~@/static/images/icons/pin.svg" alt="Carte pin" />
                                <span class="body-3 text-white">{{ product.region }}</span>
                            </div>
                            <div v-if="product.color && product.color.value" class="flex items-center gap-x-2">
                                <img src="~@/static/images/icons/glass.svg" alt="Verre de vin" />
                                <span class="body-3 text-white"
                                    >Vin {{ product.color.value }}<template v-if="product.degree"> - {{ product.degree }}%</template></span
                                >
                            </div>
                            <div v-if="product.cepage" class="flex items-center gap-x-2">
                                <img src="~@/static/images/icons/grapes.svg" alt="Grappe de raisin" />
                                <span class="body-3 text-white">{{ product.cepage }}</span>
                            </div>
                        </div>
                        <div class="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 mt-6 md:mt-4 md:h-[42px]">
                            <a v-if="product.link" :href="product.link" target="_blank" class="btn btn--plain-white btn--S" @click="seeProduct(product)">
                                {{ translations.productCard.productLinkLabel }}
                            </a>
                            <a :href="translations.common.storelink" target="_blank" class="ui-link text-white" @click="findStore(product)">{{ translations.common.groceryList }}</a>
                        </div>
                        <hr v-if="product.dishes && product.dishes.length > 0" class="border-white my-6" />
                        <div v-if="product.dishes && product.dishes.length > 0">
                            <p class="body-2 text-white">
                                <strong>{{ translations.productCard.dishesHeading }}</strong>
                            </p>
                            <div class="flex flex-wrap items-start gap-y-2 gap-x-3 mt-4">
                                <div v-for="(dish, index) in product.dishes" :key="`dish-${index}`" class="w-14 flex flex-col items-center gap-y-1 text-center">
                                    <img v-if="dish.value" :src="require(`@/static/images/type/${dish.value}.svg`)" alt="Plat" width="56" height="56" class="w-14 h-14" />
                                    <p class="body-4 text-white">
                                        {{ dish.label }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr class="border-white my-6" />
                    <p v-if="product.description" class="body-3 text-white">
                        {{ product.description }}
                    </p>
                    <div class="flex items-center gap-x-3 mt-4">
                        <img src="~@/static/images/sommelier.png" alt="Photo conseiller" />
                        <p class="body-3 text-white">
                            <strong>Dominique Laporte</strong><br />
                            sommelier Aldi
                        </p>
                    </div>
                </div>
            </div>
            <hr class="border-white mt-6 mb-4" />
            <div class="flex justify-center">
                <button :data-on="translations.common.seeLess" :data-off="translations.common.seeMore" class="product__see-more-button btn-text btn-text--arrow btn-text--arrow-down" @click="toggleProduct($event, null, index)">
                    {{ translations.common.seeMore }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
    import gsap from 'gsap';
    import { nextTick, onMounted, ref } from 'vue';

    //// PROPS
    const props = defineProps({
        product: { type: Object, default: () => {} },
        productsCount: { type: Number, default: 0 },
        index: { type: Number, default: 0 },
        products: { type: Array, default: () => [] },
        translations: { type: Object, default: () => {} },
    });

    //// DATA
    // const emits = defineEmits(['toggle-product']);
    const productContainer = ref(null);
    const productPreview = ref(null);
    const productFull = ref(null);
    const toast = ref(null);

    //// MOUTNED
    onMounted(() => {
        nextTick(() => {
            gsap.set(productContainer.value, {
                height: productPreview.value.clientHeight + 16,
            });
        });
    });

    //// METHODS
    const toggleProduct = (event, targetButton) => {
        window.dataLayer.push({
            event: 'assistant_sommelier_interaction',
            action: 'view_details',
            item_name: props.product.name,
        });

        // Get the button if it's an event trigger or a parameter element
        const button = targetButton ? targetButton : event.target;

        button.innerHTML = button.classList.contains('opened') ? button.getAttribute('data-off') : button.getAttribute('data-on');

        // Change the class of the button for the arrow
        button.classList.toggle('btn-text--arrow-down');
        button.classList.toggle('btn-text--arrow-up');

        // const indexToChange = window.innerWidth < 768 ? this.currentProductIndex : index;

        // Update the height of the product container to reveal/hide content
        gsap.to(productContainer.value, {
            height: button.classList.contains('opened') ? productPreview.value.clientHeight + 16 : productFull.value.clientHeight + 24,
            duration: 0.6,
            ease: 'power2.out',
        });

        // Toggle the class to confirm the state
        button.classList.toggle('opened');
    };

    // const getGlassType = (value) => {
    //     if (value < 3) {
    //         return 'neutral';
    //     } else if (value > 3) {
    //         return 'full';
    //     } else {
    //         return 'empty';
    //     }
    // };

    const seeProduct = (product) => {
        window.dataLayer.push({
            event: 'select_item',
            ecommerce: {
                item_list_id: 'recommandation_sommelier',
                item_list_name: 'Suggestions du Sommelier',
                items: [
                    {
                        item_id: product.sku ?? product.id,
                        item_name: product.name,
                        index: 1,
                    },
                ],
            },
        });
    };

    const findStore = (product) => {
        window.dataLayer.push({
            event: 'assistant_sommelier_interaction',
            action: 'find_store',
            item_name: product.name,
        });
    };

    const copyLinkToClipboard = () => {
        // Copy the link in clipboard
        navigator.clipboard.writeText(props.product.link);

        // Show toast to say it's copied
        gsap.to(toast.value, {
            display: 'block',
            opacity: 1,
            duration: 0.3,
            ease: 'power2.out',
        });

        gsap.to(toast.value, {
            display: 'none',
            opacity: 0,
            duration: 0.3,
            delay: 2.5,
            ease: 'power2.out',
        });
    };
</script>
